import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { formatNumber, formatTRY } from "@/lib/format";
import { Stat } from "@/lib/finance";

const HORIZONS = [30, 60, 90];

export default function CashflowPanel({ reload }) {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/finans/cashflow", { params: { days } }).then(({ data }) => setD(data)); }, [days, reload]);
  if (!d) return <div className="panel h-80 animate-pulse" />;
  const chart = d.buckets.map((b) => ({ name: b.label, Fatura: b.inflow_invoices, "Çek/Senet": b.inflow_instruments, Ödeme: -b.outflow, Bakiye: b.closing }));
  return (
    <div className="space-y-3" data-testid="cashflow-panel">
      <div className="flex items-center gap-2">
        {HORIZONS.map((h) => (
          <button key={h} type="button" onClick={() => setDays(h)} data-testid={`cashflow-days-${h}`}
            className={`h-8 px-3 text-[12px] rounded-full border transition-colors ${days === h ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-blue-600"}`}>{h} Gün</button>
        ))}
        <span className="text-[11px] text-slate-400 ml-2">Beklenen girişler: vadesi gelen açık faturalar + portföy/bankadaki çek-senet · Çıkışlar: planlı ödemeler + verilen çek/senet (vadesine göre)</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Bugünkü Nakit" value={formatTRY(d.opening)} hint="Kasa + banka + POS" testid="cf-opening" />
        <Stat label="Beklenen Giriş" value={formatTRY(d.totals.inflow)} hint={`Fatura ${formatTRY(d.totals.inflow_invoices)}`} tone="text-emerald-700" testid="cf-inflow" />
        <Stat label="Planlı Çıkış" value={formatTRY(d.totals.outflow)} hint={[d.totals.outflow_issued ? `Verilen çek/senet ${formatTRY(d.totals.outflow_issued)}` : "", d.totals.outflow_prim_est ? `Tahmini prim ${formatTRY(d.totals.outflow_prim_est)}` : ""].filter(Boolean).join(" · ")} tone="text-rose-600" testid="cf-outflow" />
        <Stat label={`${days}. Gün Tahmini`} value={formatTRY(d.totals.closing)} tone={d.totals.closing < 0 ? "text-rose-600" : "text-blue-700"}
          hint={Object.entries(d.closing_at).map(([k, v]) => `${k}g: ${formatNumber(v / 1e6, 2)} Mn`).join(" · ")} testid="cf-closing" />
        <Stat label="Vadesi Geçmiş Alacak" value={formatTRY(d.overdue_receivables)} hint="Projeksiyona dahil değil" tone="text-amber-700" testid="cf-overdue" />
      </div>
      <div className="panel p-4">
        <div className="h-72 min-h-[288px] min-w-0" data-testid="cashflow-chart">
          <ResponsiveContainer width="100%" height={288} minWidth={0}>
            <ComposedChart data={chart} margin={{ left: 10 }} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => `${formatNumber(v / 1e6, 1)} Mn`} />
              <Tooltip formatter={(v) => formatTRY(Math.abs(v))} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Fatura" stackId="a" fill="#2563eb" />
              <Bar dataKey="Çek/Senet" stackId="a" fill="#6366f1" />
              <Bar dataKey="Ödeme" stackId="a" fill="#e11d48" />
              <Line type="monotone" dataKey="Bakiye" stroke="#0f172a" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="panel overflow-x-auto">
        <table className="erp-table" data-testid="cashflow-table">
          <thead><tr><th>Hafta</th><th className="text-right">Fatura Tahsilatı</th><th className="text-right">Çek/Senet</th><th className="text-right">Planlı Ödeme</th><th className="text-right">Net</th><th className="text-right">Dönem Sonu Bakiye</th></tr></thead>
          <tbody>{d.buckets.map((b) => (
            <tr key={b.start} data-testid={`cashflow-row-${b.start}`}>
              <td className="font-mono text-xs">{b.label}</td>
              <td className="text-right font-mono">{formatTRY(b.inflow_invoices)}</td>
              <td className="text-right font-mono">{formatTRY(b.inflow_instruments)}</td>
              <td className="text-right font-mono text-rose-600" data-testid={`cashflow-out-${b.start}`}>{b.outflow ? formatTRY(-b.outflow) : "—"}{b.outflow_issued ? <div className="text-[10px] text-slate-500">evrak {formatTRY(b.outflow_issued)}</div> : null}</td>
              <td className={`text-right font-mono ${b.net < 0 ? "text-rose-600" : "text-emerald-700"}`}>{formatTRY(b.net)}</td>
              <td className={`text-right font-mono font-semibold ${b.closing < 0 ? "text-rose-600" : "text-slate-900"}`}>{formatTRY(b.closing)}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
